import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import './UserList.css';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Fetch users from backend
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Toggle password visibility
  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Handle edit click
  const handleEditClick = (user) => {
    setEditingUserId(user.id);
    setEditedUser({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role || '',
      is_active: user.is_active !== undefined ? user.is_active : true,
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditedUser({});
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      const response = await userAPI.updateUser(editingUserId, editedUser);
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === editingUserId ? { ...user, ...response.data } : user
      ));
      
      setEditingUserId(null);
      setEditedUser({});
    } catch (err) {
      console.error('Failed to update user:', err);
      alert(`Failed to update user: ${err.message}`);
    }
  };

  // Handle delete click
  const handleDeleteClick = (user) => {
    setConfirmDeleteId(user.id);
    setConfirmDeleteName(user.username);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (deleting) return;
    
    setDeleting(true);
    
    try {
      await userAPI.deleteUser(confirmDeleteId);
      
      // Update local state immediately
      setUsers(prev => prev.filter(user => user.id !== confirmDeleteId));
      
      // Close the confirmation modal
      setConfirmDeleteId(null);
      setConfirmDeleteName('');
      
      console.log('User deleted successfully');
      
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(`Failed to delete user: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    if (deleting) return;
    setConfirmDeleteId(null);
    setConfirmDeleteName('');
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'role-admin';
      case 'manager': return 'role-manager';
      case 'user': return 'role-user';
      case 'designer': return 'role-designer';
      case 'content creator': return 'role-content';
      default: return 'role-default';
    }
  };

  // Get status color
  const getStatusColor = (isActive) => {
    return isActive ? 'status-active' : 'status-inactive';
  };

  if (loading) {
    return (
      <div className="user-list-container">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-list-container">
        <div className="error">
          {error}
          <button onClick={fetchUsers} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-list-container">
      <div className="user-list-header">
        <h2>User Management</h2>
      </div>

      <div className="user-table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>#{user.id}</td>
                
                <td>
                  {editingUserId === user.id ? (
                    <input
                      type="text"
                      value={editedUser.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                    />
                  ) : (
                    user.username
                  )}
                </td>
                
                <td>
                  {editingUserId === user.id ? (
                    <div className="name-inputs">
                      <input
                        type="text"
                        placeholder="First Name"
                        value={editedUser.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={editedUser.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                      />
                    </div>
                  ) : (
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'
                  )}
                </td>
                
                <td>
                  {editingUserId === user.id ? (
                    <input
                      type="email"
                      value={editedUser.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  ) : (
                    user.email || '—'
                  )}
                </td>
                
                <td>
                  {editingUserId === user.id ? (
                    <select
                      value={editedUser.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                    >
                      <option value="">Select Role</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="user">User</option>
                      <option value="designer">Designer</option>
                      <option value="content creator">Content Creator</option>
                    </select>
                  ) : (
                    user.role ? (
                      <span className={`badge ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    ) : '—'
                  )}
                </td>
                
                <td>
                  {editingUserId === user.id ? (
                    <select
                      value={editedUser.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  ) : (
                    <span className={`badge ${getStatusColor(user.is_active)}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                
                <td>
                  {editingUserId === user.id ? (
                    <div className="action-buttons">
                      <button className="save-btn" onClick={handleSaveEdit}>💾</button>
                      <button className="cancel-btn" onClick={handleCancelEdit}>❌</button>
                    </div>
                  ) : (
                    <div className="action-buttons">
                      <button className="edit-btn" onClick={() => handleEditClick(user)}>✏️</button>
                      <button className="delete-btn" onClick={() => handleDeleteClick(user)}>🗑️</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <h3>Delete User</h3>
            <p>Are you sure you want to delete user <strong>"{confirmDeleteName}"</strong>?</p>
            <p className="warning">This action cannot be undone.</p>
            <div className="confirm-actions">
              <button 
                className={`btn confirm ${deleting ? 'loading' : ''}`} 
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button 
                className="btn cancel" 
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;